/**
 * CONFIGURAÇÕES › SISTEMA › ORDENS DE SERVIÇO (fase 3; decisão de 24/10/2026: a configuração da OS mora em Configurações,
 * não em Manutenção, e reaproveita os catálogos — sistemas = Catálogo de contrato, questionários = Catálogos ›
 * Questionários).
 *
 * 🔴 "Piloto" e "Avisos a clientes" nascem DESLIGADOS e só o administrador muda, com a consequência escrita antes de
 * confirmar (régua: interruptor que muda o que sai para o mundo não é um clique silencioso).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Aviso, Badge, Button, Checkbox, EmptyState, ErroCampo, Input, Label, Select, useConfirmDialog } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';

import { mensagemDoErro, osApi, type ConfigDaOs, type Natureza, type RegraDoServico, type Tecnico } from './os-api';
import { TecnicosDeCampo } from './TecnicosDeCampo';

const GRUPOS: Array<{ id: string; nome: string }> = [
  { id: 'preventiva', nome: 'Preventiva' },
  { id: 'corretiva', nome: 'Corretiva' },
  { id: 'plantao', nome: 'Plantão' },
  { id: 'visita_tecnica', nome: 'Visita técnica' },
  { id: 'acesso_remoto', nome: 'Acesso remoto' },
  { id: 'implantacao', nome: 'Implantação' },
  { id: 'outros', nome: 'Outros' },
];

interface RegraEmEdicao {
  naturezaId: string;
  sistemaId: string;
  exigeRelato: boolean;
  exigeAssinatura: boolean;
  exigeCpfAssinante: boolean;
  fotosMinimas: string;
  questionarioTemplateId: string;
  duracaoEstimadaMin: string;
}

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-line p-4 dark:border-line-dark">
      <h2 className="text-base font-semibold">{titulo}</h2>
      {children}
    </section>
  );
}

export function ConfiguracaoDaOs() {
  const { show } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;
  const confirmar = useConfirmDialog();
  const [naturezas, setNaturezas] = useState<Natureza[]>([]);
  const [regras, setRegras] = useState<RegraDoServico[]>([]);
  const [sistemas, setSistemas] = useState<Array<{ id: string; nome: string }>>([]);
  const [config, setConfig] = useState<ConfigDaOs | null>(null);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [nova, setNova] = useState({ nome: '', grupo: 'corretiva' });
  const [erroNova, setErroNova] = useState<string | undefined>();
  const [regra, setRegra] = useState<RegraEmEdicao | null>(null);
  const [erroRegra, setErroRegra] = useState<string | undefined>();
  const [distancia, setDistancia] = useState('');
  const [escada, setEscada] = useState(['', '', '']);
  const [erroConfig, setErroConfig] = useState<string | undefined>();

  const carregar = useCallback(async () => {
    try {
      const [n, r, s, c, t] = await Promise.all([osApi.naturezas(), osApi.regras(), osApi.sistemas(), osApi.config(), osApi.tecnicos()]);
      setNaturezas(n);
      setRegras(r);
      setSistemas(s);
      setConfig(c);
      setTecnicos(t);
      setDistancia(String(c.distanciaCheckinM));
      setEscada(c.escadaMin.map(String));
      setErro(null);
    } catch (e) {
      setErro(mensagemDoErro(e, 'Não deu para abrir a configuração.'));
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function agir(fn: () => Promise<unknown>, ok: string, aoErrar: (m: string) => void) {
    try {
      await fn();
      show(ok, { variant: 'success' });
      await carregar();
    } catch (e) {
      aoErrar(mensagemDoErro(e));
    }
  }

  /** Interruptor que muda o que sai para o mundo pergunta antes, com a consequência escrita. */
  async function mudarChave(qual: 'piloto', ligado: boolean) {
    const ok = await confirmar({
      title: ligado ? 'Ligar o piloto?' : 'Desligar o piloto?',
      message: ligado
        ? 'Os técnicos marcados passam a receber as OS no aplicativo. Os outros continuam no Auvo.'
        : 'Ninguém mais abre o aplicativo do técnico, nem os marcados: eles voltam para o Auvo. Fica registrado quem mudou e quando.',
      confirmLabel: 'Confirmar',
    });
    if (ok) await agir(() => osApi.mudarChave(qual, ligado), 'Mudança registrada.', (m) => show(m, { variant: 'error' }));
  }

  const nomeDoSistema = (id: string | null) => (id ? (sistemas.find((s) => s.id === id)?.nome ?? id) : 'Todos os sistemas');
  const nomeDaNatureza = (id: string) => naturezas.find((n) => n.id === id)?.nome ?? id;

  if (erro) return <EmptyState title={erro} />;

  return (
    <div className="flex flex-col gap-4" data-testid="configuracao-os">
      <Bloco titulo="Serviços">
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Os tipos de visita. “Conta como” é o que os indicadores da Manutenção usam para agrupar.</p>
        <ul className="flex flex-col gap-1">
          {naturezas.map((n) => (
            <li key={n.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span>
                {n.nome} <span className="text-ink-3 dark:text-ink-dark-3">· conta como {GRUPOS.find((g) => g.id === n.grupo)?.nome ?? n.grupo}</span>
                {!n.ativo ? (
                  <Badge variant="neutral" className="ml-2">
                    Desativado
                  </Badge>
                ) : null}
              </span>
              <Button size="sm" variant="ghost" onClick={() => agir(() => osApi.editarNatureza(n.id, { ativo: !n.ativo }), n.ativo ? `${n.nome} desativado.` : `${n.nome} reativado.`, (m) => show(m, { variant: 'error' }))}>
                {n.ativo ? 'Desativar' : 'Reativar'}
              </Button>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_200px_auto]">
          <div>
            <Label htmlFor="nova-natureza">Novo serviço</Label>
            <Input id="nova-natureza" value={nova.nome} onChange={(e) => setNova({ ...nova, nome: e.target.value })} placeholder="Por exemplo: Vistoria" error={erroNova} />
          </div>
          <div>
            <Label htmlFor="nova-natureza-grupo">Conta como</Label>
            <Select id="nova-natureza-grupo" value={nova.grupo} onChange={(e) => setNova({ ...nova, grupo: e.target.value })}>
              {GRUPOS.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.nome}
                </option>
              ))}
            </Select>
          </div>
          <Button
            onClick={() =>
              agir(
                async () => {
                  setErroNova(undefined);
                  await osApi.criarNatureza(nova);
                  setNova({ nome: '', grupo: 'corretiva' });
                },
                'Serviço criado.',
                setErroNova,
              )
            }
          >
            Criar serviço
          </Button>
        </div>
      </Bloco>

      <Bloco titulo="O que cada serviço exige">
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">
          A regra de um sistema vale só para ele; a de “Todos os sistemas” vale para o resto. Sem regra, o técnico só precisa do check-in e do check-out.
        </p>
        <ul className="flex flex-col gap-1 text-sm">
          {regras.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <strong>{nomeDaNatureza(r.naturezaId)}</strong> · {nomeDoSistema(r.sistemaId)}: {r.fotosMinimas} foto(s)
                {r.exigeRelato ? ', relato' : ''}
                {r.exigeAssinatura ? ', assinatura' : ''}
                {r.exigeCpfAssinante ? ' com CPF' : ''}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setRegra({
                    naturezaId: r.naturezaId,
                    sistemaId: r.sistemaId ?? '',
                    exigeRelato: r.exigeRelato,
                    exigeAssinatura: r.exigeAssinatura,
                    exigeCpfAssinante: r.exigeCpfAssinante,
                    fotosMinimas: String(r.fotosMinimas),
                    questionarioTemplateId: r.questionarioTemplateId ?? '',
                    duracaoEstimadaMin: r.duracaoEstimadaMin ? String(r.duracaoEstimadaMin) : '',
                  })
                }
              >
                Editar
              </Button>
            </li>
          ))}
        </ul>
        {regra ? (
          <div className="flex flex-col gap-3 rounded-lg border border-line p-3 dark:border-line-dark">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="regra-natureza">Serviço</Label>
                <Select id="regra-natureza" value={regra.naturezaId} onChange={(e) => setRegra({ ...regra, naturezaId: e.target.value })}>
                  {naturezas.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.nome}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="regra-sistema">Sistema</Label>
                <Select id="regra-sistema" value={regra.sistemaId} onChange={(e) => setRegra({ ...regra, sistemaId: e.target.value })}>
                  <option value="">Todos os sistemas</option>
                  {sistemas.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nome}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="regra-fotos">Mínimo de fotos</Label>
                <Input id="regra-fotos" type="number" min={0} max={50} value={regra.fotosMinimas} onChange={(e) => setRegra({ ...regra, fotosMinimas: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="regra-duracao">Duração estimada (minutos)</Label>
                <Input id="regra-duracao" type="number" min={1} value={regra.duracaoEstimadaMin} onChange={(e) => setRegra({ ...regra, duracaoEstimadaMin: e.target.value })} placeholder="90" />
              </div>
              {/* D60 (mig 794): o checklist saiu da regra do serviço e foi para a ficha de cada cliente. */}
              <p className="text-sm text-ink-3 sm:col-span-2 dark:text-ink-dark-3">O checklist do atendimento é de cada cliente: ficha do cliente › Checklist do atendimento.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Checkbox label="Exige relato" checked={regra.exigeRelato} onChange={(e) => setRegra({ ...regra, exigeRelato: e.target.checked })} />
              <Checkbox label="Exige assinatura" checked={regra.exigeAssinatura} onChange={(e) => setRegra({ ...regra, exigeAssinatura: e.target.checked, exigeCpfAssinante: e.target.checked && regra.exigeCpfAssinante })} />
              <Checkbox label="Pede o CPF de quem assina" checked={regra.exigeCpfAssinante} disabled={!regra.exigeAssinatura} onChange={(e) => setRegra({ ...regra, exigeCpfAssinante: e.target.checked })} />
            </div>
            <ErroCampo id="regra" mensagem={erroRegra} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRegra(null)}>
                Cancelar
              </Button>
              <Button
                onClick={() =>
                  agir(
                    async () => {
                      setErroRegra(undefined);
                      await osApi.salvarRegra({
                        naturezaId: regra.naturezaId,
                        sistemaId: regra.sistemaId || null,
                        exigeRelato: regra.exigeRelato,
                        exigeAssinatura: regra.exigeAssinatura,
                        exigeCpfAssinante: regra.exigeCpfAssinante,
                        fotosMinimas: Number(regra.fotosMinimas || 0),
                        questionarioTemplateId: regra.questionarioTemplateId || null,
                        duracaoEstimadaMin: regra.duracaoEstimadaMin ? Number(regra.duracaoEstimadaMin) : null,
                      });
                      setRegra(null);
                    },
                    'Regra salva.',
                    setErroRegra,
                  )
                }
              >
                Salvar regra
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="secondary"
            onClick={() =>
              setRegra({ naturezaId: naturezas[0]?.id ?? '', sistemaId: '', exigeRelato: true, exigeAssinatura: true, exigeCpfAssinante: false, fotosMinimas: '0', questionarioTemplateId: '', duracaoEstimadaMin: '' })
            }
          >
            Nova regra
          </Button>
        )}
      </Bloco>

      <Bloco titulo="Check-in e lembrete do check-out">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <Label htmlFor="cfg-distancia">Distância do check-in (metros)</Label>
            <Input id="cfg-distancia" type="number" value={distancia} onChange={(e) => setDistancia(e.target.value)} hint="Acima disto, o técnico justifica." />
          </div>
          {['1º lembrete após o check-in', '2º lembrete depois do 1º', '3º lembrete depois do 2º'].map((rot, i) => (
            <div key={rot}>
              <Label htmlFor={`cfg-escada-${i}`}>{rot} (minutos)</Label>
              <Input id={`cfg-escada-${i}`} type="number" value={escada[i]} onChange={(e) => setEscada(escada.map((v, j) => (j === i ? e.target.value : v)))} />
            </div>
          ))}
        </div>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Depois do 3º lembrete sem check-out, o técnico não consegue começar outra OS até informar a saída.</p>
        <ErroCampo id="cfg" mensagem={erroConfig} />
        <div>
          <Button
            onClick={() =>
              agir(
                async () => {
                  setErroConfig(undefined);
                  await osApi.salvarConfig({ distanciaCheckinM: Number(distancia), escadaMin: [Number(escada[0]), Number(escada[1]), Number(escada[2])] });
                },
                'Configuração salva.',
                setErroConfig,
              )
            }
          >
            Salvar
          </Button>
        </div>
      </Bloco>

      <Bloco titulo="Técnicos">
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">
          Quem está no departamento Técnico. Os marcados usam o aplicativo das ordens de serviço; os outros continuam no Auvo.
        </p>
        {tecnicos.length === 0 ? (
          <p className="text-sm text-ink-3 dark:text-ink-dark-3" data-testid="tecnicos-vazio">
            Ninguém no departamento Técnico ainda. Ponha as pessoas no departamento em Configurações › Usuários, ou convide
            quem não tem e-mail da empresa logo abaixo.
          </p>
        ) : null}
        {/* Marcar quem usa o aplicativo é só do administrador, como o interruptor do piloto (o servidor confere igual). */}
        <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {tecnicos.map((t) => (
            <li key={t.userId}>
              <Checkbox
                label={t.nome}
                checked={t.noPiloto}
                disabled={!isAdmin}
                onChange={(e) => agir(() => osApi.marcarPiloto(t.userId, e.target.checked), e.target.checked ? `${t.nome} passa a usar as ordens de serviço.` : `${t.nome} volta para o Auvo.`, (m) => show(m, { variant: 'error' }))}
              />
            </li>
          ))}
        </ul>
        {config && !config.pilotoLigado && tecnicos.some((t) => t.noPiloto) ? (
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">Os marcados só passam a usar o aplicativo quando o piloto for ligado.</p>
        ) : null}
      </Bloco>

      {isAdmin ? <TecnicosDeCampo aoMudar={() => void carregar()} /> : null}

      {config ? (
        <Bloco titulo="Piloto">
          <ul className="flex flex-col gap-2 text-sm">
            <li className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Piloto: <strong>{config.pilotoLigado ? 'ligado' : 'desligado'}</strong>
              </span>
              {isAdmin ? (
                <Button size="sm" variant="secondary" onClick={() => void mudarChave('piloto', !config.pilotoLigado)}>
                  {config.pilotoLigado ? 'Desligar o piloto' : 'Ligar o piloto'}
                </Button>
              ) : null}
            </li>
            {/* D73 (27/10/2026): os avisos da OS ao cliente se ligam um por um na tela do WhatsApp, a que já existe. */}
            <li>
              <Link
                to="/admin/configuracoes/integracoes/whatsapp"
                className="inline-flex min-h-[44px] items-center text-sm font-semibold text-red hover:underline md:min-h-0"
                data-testid="avisos-da-os-no-whatsapp"
              >
                Os avisos da OS aos clientes se ligam em Integrações › WhatsApp ›
              </Link>
            </li>
          </ul>
          {!isAdmin ? <Aviso fato="Só o administrador liga ou desliga o piloto." /> : null}
          {config.atualizadoPor ? <p className="text-xs text-ink-3 dark:text-ink-dark-3">Última mudança: {config.atualizadoPor}.</p> : null}
        </Bloco>
      ) : null}

    </div>
  );
}
